import { ApiError, apiDelete, apiFetch, parseProblem, API_BASE_URL } from "@/lib/api-client";
import { reportingPeriod, emergencyMap } from "@duress/shared";

export async function listPeriods(
  clientId: string,
  cursor?: string,
): Promise<reportingPeriod.ReportingPeriodListResponse> {
  const params = new URLSearchParams({ limit: "50" });
  if (cursor) params.set("cursor", cursor);
  return apiFetch(
    `/clients/${clientId}/periods?${params.toString()}`,
    reportingPeriod.ReportingPeriodListResponseSchema,
  );
}

export interface UploadPeriodFiles {
  usageCsv?: File | undefined;
  emergencyCsv?: File | undefined;
  checkinCsv?: File | undefined;
  teamAlertCsv?: File | undefined;
}

// Shared by both upload paths below: fetch() rejects outright (no response
// to parse) when the connection drops mid-upload -- a file whose processing
// exceeds the server's budget produces exactly this instead of a normal
// HTTP error. The browser's own message here ("Failed to fetch") gives the
// user no indication of what happened or what to do about it.
async function fetchOrExplain(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new Error(
      "The upload didn't complete — this can happen with very large files if processing takes too long. Try uploading fewer months at once.",
    );
  }
}

// Feature CSVs only (no usage file) — small, processed synchronously, still
// goes through the original multipart endpoint. Also used as the "attach
// feature CSVs" half of a combined upload (see uploadPeriod below).
async function uploadFeatureFiles(
  clientId: string,
  files: Omit<UploadPeriodFiles, "usageCsv">,
): Promise<reportingPeriod.UploadPeriodResponse> {
  const formData = new FormData();
  if (files.emergencyCsv) formData.append("emergency_csv", files.emergencyCsv);
  if (files.checkinCsv) formData.append("checkin_csv", files.checkinCsv);
  if (files.teamAlertCsv) formData.append("team_alert_csv", files.teamAlertCsv);

  const res = await fetchOrExplain(`${API_BASE_URL}/v1/clients/${clientId}/periods`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!res.ok) throw new ApiError(res.status, await parseProblem(res));
  const json: unknown = await res.json();
  return reportingPeriod.UploadPeriodResponseSchema.parse(json);
}

// The usage file goes as a raw (non-multipart) request body to its own
// endpoint, streamed directly off the network into the parser -- multipart
// parsing (used by uploadFeatureFiles above) buffers the whole file into
// memory *twice* before any processing starts, which is exactly what
// exceeded a Worker's 128MB limit on a real ~85MB export. Passing a File
// directly as fetch's body streams it; nothing here reads it into memory
// first.
async function uploadUsageFile(
  clientId: string,
  file: File,
): Promise<reportingPeriod.AsyncUploadResponse> {
  const res = await fetchOrExplain(`${API_BASE_URL}/v1/clients/${clientId}/periods/usage`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new ApiError(res.status, await parseProblem(res));
  const json: unknown = await res.json();
  return reportingPeriod.AsyncUploadResponseSchema.parse(json);
}

// A feature-only upload is processed synchronously and returns the finished
// UploadPeriodResponse (HTTP 201). A usage CSV/XLSX upload is instead
// partitioned by month and processed via a Cloudflare Queue (see the
// backend's PeriodService.partitionUsageUpload) — it returns an
// AsyncUploadResponse (HTTP 202) with a jobId to poll via
// getUploadJobStatus. Callers tell the two apart with `"jobId" in result`.
//
// When both a usage file and feature CSVs are provided, the feature CSVs
// are attached first (small, synchronous, independent of the usage file's
// own month-detection — each feature dataset is grouped by its own date
// column) and the usage file's async job is what the caller ends up
// polling; the overall upload is only "done" once that job completes.
export async function uploadPeriod(
  clientId: string,
  files: UploadPeriodFiles,
): Promise<reportingPeriod.UploadOutcome> {
  if (!files.usageCsv) return uploadFeatureFiles(clientId, files);

  if (files.emergencyCsv || files.checkinCsv || files.teamAlertCsv) {
    await uploadFeatureFiles(clientId, files);
  }
  return uploadUsageFile(clientId, files.usageCsv);
}

export async function getUploadJobStatus(
  clientId: string,
  jobId: string,
): Promise<reportingPeriod.UploadJobStatus> {
  return apiFetch(
    `/clients/${clientId}/upload-jobs/${jobId}`,
    reportingPeriod.UploadJobStatusSchema,
  );
}

// Independent of any particular dialog instance's in-memory jobId — see
// useActiveUploadJob for why this is polled on its own.
export async function getActiveUploadJob(
  clientId: string,
): Promise<reportingPeriod.ActiveUploadJob> {
  return apiFetch(
    `/clients/${clientId}/upload-jobs/active`,
    reportingPeriod.ActiveUploadJobSchema,
  );
}

export async function computeDashboard(
  clientId: string,
  range?: { from: string; to: string },
): Promise<reportingPeriod.ComputedDashboardResponse> {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return apiFetch(
    `/clients/${clientId}/sessions/compute${qs}`,
    reportingPeriod.ComputedDashboardResponseSchema,
  );
}

export async function getPeriod(
  clientId: string,
  periodId: string,
): Promise<reportingPeriod.PeriodDetail> {
  return apiFetch(`/clients/${clientId}/periods/${periodId}`, reportingPeriod.PeriodDetailSchema);
}

export async function getEmergencyMapForPeriod(
  clientId: string,
  periodId: string,
): Promise<emergencyMap.EmergencyMapResponse> {
  return apiFetch(
    `/clients/${clientId}/periods/${periodId}/emergency-map`,
    emergencyMap.EmergencyMapResponseSchema,
  );
}

export async function computeEmergencyMap(
  clientId: string,
  range?: { from: string; to: string },
): Promise<emergencyMap.EmergencyMapResponse> {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return apiFetch(
    `/clients/${clientId}/emergency-map/compute${qs}`,
    emergencyMap.EmergencyMapResponseSchema,
  );
}

export async function getTrends(clientId: string): Promise<reportingPeriod.PeriodTrendsResponse> {
  return apiFetch(
    `/clients/${clientId}/periods/trends`,
    reportingPeriod.PeriodTrendsResponseSchema,
  );
}

export async function getDailyActivity(
  clientId: string,
  periodId: string,
): Promise<reportingPeriod.DailyActivityResponse> {
  return apiFetch(
    `/clients/${clientId}/periods/${periodId}/daily-activity`,
    reportingPeriod.DailyActivityResponseSchema,
  );
}

export async function getTeamDeviceBreakdown(
  clientId: string,
  periodId: string,
  team: string,
): Promise<reportingPeriod.TeamDeviceBreakdownResponse> {
  const params = new URLSearchParams({ team });
  return apiFetch(
    `/clients/${clientId}/periods/${periodId}/devices?${params.toString()}`,
    reportingPeriod.TeamDeviceBreakdownResponseSchema,
  );
}

export async function deletePeriod(clientId: string, periodId: string): Promise<void> {
  await apiDelete(
    `/clients/${encodeURIComponent(clientId)}/periods/${encodeURIComponent(periodId)}`,
  );
}
